import { EvidenceSubmissionCreateData } from '@/backend/access/evidenceSubmission/types';
import { EvidenceSubmission, ReviewerOutcome, SubmitterOutcome } from '@/backend/models/evidenceSubmission';
import { create } from 'zustand';
import { EvidenceSubmissionController } from '../controllers/evidenceSubmissionController';
import { RequirementEvidenceCategoryController } from '@/frontend/controllers/requirementEvidenceCategoryController';
import { RequirementEvidenceCategory } from '@/backend/models/requirementEvidenceCategory';
import { useGlobalStore } from './GlobalStore';
import { EvidenceItemController } from '../controllers/evidenceItemController';
import { EvidenceItem } from '@/backend/models/evidenceItem';
import { RequirementEvidenceCategoryCreateData } from '@/backend/access/requirementEvidenceCategory/types';

interface RequirementDocumentationStore {
  /** Categories keyed by requirement id */
  categoriesByRequirement: Record<string, RequirementEvidenceCategory[]>;
  /** Submissions keyed by category id */
  submissionsByCategory: Record<string, EvidenceSubmission[]>;
  /**
   * Files behind the submissions visible on the current requirement — which includes other orgs'
   * submitted evidence and their designs. Use this to render a submission, never to offer a file
   * to attach.
   */
  evidenceItems: EvidenceItem[];
  /** This org's file pool for the current project: the only source the attach picker may offer. */
  projectEvidenceItems: EvidenceItem[];

  fetchCategories: (requirementId: string) => Promise<void>;
  fetchEvidenceItemsByProjectId: (projectId: string) => Promise<void>;
  createCategory: (data: RequirementEvidenceCategoryCreateData) => Promise<void>;
  updateCategory: (requirementId: string, categoryId: string, data: { name: string; description?: string | null }) => Promise<void>;
  deleteCategory: (requirementId: string, categoryId: string) => Promise<void>;
  createSubmission: (data: EvidenceSubmissionCreateData, onProgress: (percent: number) => void, file?: File) => Promise<void>;
  updateSubmitterOutcome: (outcomes: Record<string, SubmitterOutcome>) => Promise<void>;
  updateReviewerOutcome: (submissionId: string, reviewerOutcome: ReviewerOutcome, reviewComment: string) => Promise<void>;
  reset: () => void;
}

const initialState = {
  categoriesByRequirement: {} as Record<string, RequirementEvidenceCategory[]>,
  submissionsByCategory: {} as Record<string, EvidenceSubmission[]>,
  evidenceItems: [] as EvidenceItem[],
  projectEvidenceItems: [] as EvidenceItem[],
};

export const useRequirementDocumentationStore = create<RequirementDocumentationStore>((set, get) => ({
  ...initialState,

  fetchCategories: async (requirementId: string) => {
    const result = await new RequirementEvidenceCategoryController().fetchCategories(requirementId);
    if (result.status !== 200 || !result.categories) return;
    const categories = result.categories.map((c) => new RequirementEvidenceCategory(c));
    const submissions = result.submissions.map((s) => new EvidenceSubmission(s));
    const submissionsByCategory = get().submissionsByCategory;
    categories.forEach((c) => {
      submissionsByCategory[c.id] = [];
    });
    submissions.forEach((s) => {
      submissionsByCategory[s.evidenceCategoryId].push(s);
    });
    const existingItems = get().evidenceItems;
    const existingIds = new Set(existingItems.map((i) => i.id));
    const newItems = result.evidenceItems
      .filter((i: { id: string }) => !existingIds.has(i.id))
      .map((i) => new EvidenceItem(i));
    set({
      categoriesByRequirement: {
        ...get().categoriesByRequirement,
        [requirementId]: categories,
      },
      submissionsByCategory,
      evidenceItems: [...existingItems, ...newItems],
    });
  },
  fetchEvidenceItemsByProjectId: async (projectId: string) => {
    const result = await new EvidenceItemController().readByProjectId(projectId);
    if (result.status !== 200 || !result.evidenceItems) return;
    set({ projectEvidenceItems: result.evidenceItems.map((i) => new EvidenceItem(i)) });
  },
  createCategory: async (data: RequirementEvidenceCategoryCreateData) => {
    const current = get().categoriesByRequirement[data.requirementId] ?? [];
    const result = await new RequirementEvidenceCategoryController().createCategory(data);
    if (result.status !== 201 || result.category === null) return;
    const category = new RequirementEvidenceCategory(result.category);

    set({
      categoriesByRequirement: {
        ...get().categoriesByRequirement,
        [data.requirementId]: [...current, category],
      },
      submissionsByCategory: {
        ...get().submissionsByCategory,
        [category.id]: [],
      },
    });
  },
  updateCategory: async (requirementId: string, categoryId: string, data: { name: string; description?: string | null }) => {
    const result = await new RequirementEvidenceCategoryController().updateCategory(categoryId, {
      name: data.name,
      description: data.description ?? null,
    });
    if (result.status !== 200 || !result.category) return;
    const updated = new RequirementEvidenceCategory(result.category);
    const current = get().categoriesByRequirement[requirementId] ?? [];
    set({
      categoriesByRequirement: {
        ...get().categoriesByRequirement,
        [requirementId]: current.map((c) => (c.id === categoryId ? updated : c)),
      },
    });
  },
  deleteCategory: async (requirementId: string, categoryId: string) => {
    const result = await new RequirementEvidenceCategoryController().deleteCategory(categoryId);
    if (result.status !== 200) return;

    const current = get().categoriesByRequirement[requirementId] ?? [];
    const { [categoryId]: _removed, ...remainingSubmissions } = get().submissionsByCategory;
    set({
      categoriesByRequirement: {
        ...get().categoriesByRequirement,
        [requirementId]: current.filter((c) => c.id !== categoryId),
      },
      submissionsByCategory: remainingSubmissions,
    });
  },

  createSubmission: async (data: EvidenceSubmissionCreateData, onProgress: (percent: number) => void, file?: File) => {
    const response = await new EvidenceSubmissionController().create(data);
    if (!response.evidenceSubmission) return;

    if (file && response.uploadUrl && response.evidenceItem) {
      const confirmed = await useGlobalStore.getState().uploadFile(response.evidenceItem.file.id, response.uploadUrl, file, onProgress);
      if (!confirmed) {
        await new EvidenceSubmissionController().delete(response.evidenceSubmission.id);
        await new EvidenceItemController().delete(response.evidenceSubmission.evidenceItemId);
        return;
      }
      // A file we just uploaded belongs in both pools: it renders the new submission, and it joins
      // this org's project pool so the picker can reuse it.
      const created = new EvidenceItem(response.evidenceItem);
      set({
        evidenceItems: [...get().evidenceItems, created],
        projectEvidenceItems: [...get().projectEvidenceItems, created],
      });
    }

    const currentSubmissions = get().submissionsByCategory[data.evidenceCategoryId] ?? [];
    set({
      submissionsByCategory: {
        ...get().submissionsByCategory,
        [response.evidenceSubmission.evidenceCategoryId]: [new EvidenceSubmission(response.evidenceSubmission), ...currentSubmissions],
      },
    });
  },

  updateSubmitterOutcome: async (outcomes) => {
    const response = await new EvidenceSubmissionController().updateSubmitterOutcome(outcomes);
    if (response.submissions?.length) {
      const updated = response.submissions.map((s) => new EvidenceSubmission(s));
      const updatedById = new Map(updated.map((s) => [s.id, s]));
      const current = get().submissionsByCategory;
      const next = { ...current };
      for (const [categoryId, subs] of Object.entries(current)) {
        const hasUpdate = subs.some((s) => updatedById.has(s.id));
        if (hasUpdate) {
          next[categoryId] = subs.map((s) => updatedById.get(s.id) ?? s);
        }
      }
      set({ submissionsByCategory: next });
    }
  },
  updateReviewerOutcome: async (submissionId: string, reviewerOutcome: ReviewerOutcome, reviewComment: string) => {
    const response = await new EvidenceSubmissionController().updateReviewerOutcome(submissionId, reviewerOutcome, reviewComment || '');
    const { evidenceSubmission } = response;
    if (evidenceSubmission) {
      const currentSubmissions = get().submissionsByCategory[evidenceSubmission.evidenceCategoryId] ?? [];
      set({
        submissionsByCategory: {
          ...get().submissionsByCategory,
          [evidenceSubmission.evidenceCategoryId]: currentSubmissions.map((s) => s.id === submissionId ? new EvidenceSubmission(evidenceSubmission) : s),
        },
      });
    }
  },
  reset: () => set(initialState),
}));
